import { arrowUndoOutline, createOutline } from 'ionicons/icons';
import { cloneDeep, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useWithConfirmation } from '../../../../../../../components/button/with-confirmation';
import { useScratchpadServiceContext } from '../../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { logger } from '../../../../../../../util/logger';
import {
  FlowUpdateMethod,
  useFlowRefContext,
} from '../../../../../shared/component/flow/flow-ref';
import { FlowItemBaseAction } from '../../../../../shared/component/flow-item/action/flow-item-base-action';
import { FlowItemCopyAction } from '../../../../../shared/component/flow-item/action/flow-item-copy-action';
import { FlowItemDeleteAction } from '../../../../../shared/component/flow-item/action/flow-item-delete-action';
import { FlowSectionEditActionBarProps } from '../../../../../shared/component/flow-item/edit/flow-section-edit-action-bar';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import { FlowItem } from '../../../../../shared/model/flow.types';
import {
  createSectionsFromFlowItems,
  instanceOfFlowItem,
} from '../../../../../shared/util/flow-item.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';

const WeldingFlowSectionStitchActionBar = (
  props: Omit<FlowSectionEditActionBarProps, 'sectionItem'> & {
    sectionItem: BeaconWeldingData.FlowItemStitchSectionParent;
  }
) => {
  const { sectionItem, readOnly, onDelete, flowItems, onClosed } = props;

  const { flowAnalyticService } = useFlowServices();
  const { saveFlow, componentRegistry } = useFlowRefContext();
  const { quickActions } = componentRegistry;
  const { scratchpad } = useScratchpadServiceContext();

  const sections = createSectionsFromFlowItems(flowItems);
  const childSections = useMemo(
    () => sections.filter((s) => s?.section?.parentItem?.id === sectionItem.id),
    [sectionItem.id, sections]
  );

  const allStitchSegments = useMemo(() => {
    const items = [
      ...(childSections.map((s) => s.section?.item) ?? []),
    ] as FlowItem[];

    return cloneDeep(items);
  }, [childSections]);

  const allStitchItems = useMemo(() => {
    const childItems = childSections.flatMap((s) => {
      const items = s.items.map((i) => i.item);
      const sectionItem = s?.section?.item;

      return sectionItem ? [sectionItem, ...items] : items;
    });

    return [sectionItem, ...childItems] as FlowItem[];
  }, [childSections, sectionItem]);

  const originItemId = useMemo(
    () =>
      sectionItem.attributes.originalItems.find(instanceOfFlowItem)?.id ??
      'unknown',
    [sectionItem.attributes.originalItems]
  );

  const withConfirmation = useWithConfirmation({
    header: getLocalizedText('common:stitch_undo_confirmation'),
  });

  const undoStitch = useCallback(async () => {
    const clonedItems = cloneDeep(flowItems);

    const startIndex = clonedItems.findIndex((i) => i.id === sectionItem.id);
    const endIndex = clonedItems.findIndex(
      (i) => i.id === allStitchItems[allStitchItems.length - 1].id
    );

    clonedItems.splice(
      startIndex,
      endIndex - startIndex + 1,
      ...((sectionItem.attributes.originalItems as FlowItem[]) ?? [])
    );

    flowAnalyticService.trackStitchUndo({
      scratchpadId: scratchpad.id,
      sectionItemId: sectionItem.id,
      originItemId:
        sectionItem.attributes.originalItems.find(instanceOfFlowItem)?.id ??
        'unknown',
    });

    await saveFlow(clonedItems, {
      updateMethod: FlowUpdateMethod.SERVER,
      saveAsDelta: true,
    });
  }, [
    allStitchItems,
    flowAnalyticService,
    flowItems,
    saveFlow,
    scratchpad.id,
    sectionItem.attributes.originalItems,
    sectionItem.id,
  ]);

  const handleUndo = () => {
    withConfirmation({
      onConfirmation: async () => {
        await undoStitch();
        if (onClosed) {
          onClosed();
        }
      },
    });
  };

  return (
    <div className="flow-section-edit-action-bar">
      <FlowItemBaseAction
        label={getLocalizedText('common:stitch_undo')}
        icon={arrowUndoOutline}
        disabled={readOnly}
        onClick={handleUndo}
        color="var(--ion-text-color)"
      />
      <FlowItemBaseAction
        icon={createOutline}
        label={getLocalizedText('common:stitch_edit')}
        onClick={() => {
          const hasConvertToStitchHandler =
            quickActions &&
            !isNil(
              quickActions[
                BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_STITCH
              ]
            );

          if (hasConvertToStitchHandler) {
            const { handler } =
              quickActions[
                BeaconWeldingData.FlowItemQuickAction.CONVERT_TO_STITCH
              ];
            handler(sectionItem, onClosed);
          } else {
            logger.error('No handler for stitch conversion', {
              quickActions: Object.keys(quickActions ?? {}),
            });
          }
        }}
      />
      <FlowItemCopyAction
        label={`${getLocalizedText('common:copy_all')} (${
          allStitchSegments.length
        })`}
        items={allStitchItems}
        onCopy={() => {
          flowAnalyticService.trackStitchCopy();
        }}
      />
      <FlowItemDeleteAction
        label={`${getLocalizedText('common:delete_all')} (${
          allStitchSegments.length
        })`}
        disabled={readOnly}
        onClick={() => {
          if (onDelete) {
            flowAnalyticService.trackStitchDelete({
              scratchpadId: scratchpad.id,
              sectionItemId: sectionItem.id,
              originItemId,
            });
            onDelete(allStitchItems);
          }
        }}
      />
    </div>
  );
};

export { WeldingFlowSectionStitchActionBar };
